import { useState } from 'react';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { X as XIcon } from 'react-feather';
import { Constants } from '@trinity/chat-components/components/Theme';

export interface SurveyQuestion {
  key: string;
  text: string;
}

export interface SurveyMessage {
  id: number;
  title: string;
  body: string;
  category: string | null;
}

export interface SurveyFormData {
  questions: SurveyQuestion[];
  messages: SurveyMessage[];
}

const DEFAULT_QUESTIONS: SurveyQuestion[] = [
  { key: 'Q1', text: 'Please select the messages that you feel are most compelling in this set.' },
  { key: 'Q2', text: 'Please select the messages that you feel are most relevant in this set.' },
  { key: 'Q3', text: 'Please sequence these messages to make the most persuasive story. Please be somewhat selective in this exercise. You may, however, include as many or as few messages as you would like.' },
  { key: 'Q4', text: 'Please rate the story you created on the following dimensions.' },
  { key: 'Q5', text: 'Please select up to 5 words that you most associate with the story you created.' },
];

type Props = {
  initialMessages: SurveyMessage[];
  initialQuestions?: SurveyQuestion[];
  onSave: (data: SurveyFormData) => void;
  onCancel: () => void;
};

export const SurveyConfigForm = ({ initialMessages, initialQuestions, onSave, onCancel }: Props) => {
  const [questions, setQuestions] = useState<SurveyQuestion[]>(
    initialQuestions ?? DEFAULT_QUESTIONS
  );
  const [surveyMessages, setSurveyMessages] = useState<SurveyMessage[]>(initialMessages);

  const updateQuestion = (index: number, text: string) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, text } : q)));
  };

  const removeMessage = (messageId: number) => {
    setSurveyMessages((prev) => prev.filter((m) => m.id !== messageId));
  };

  const handleSave = () => {
    if (surveyMessages.length === 0) return;
    onSave({ questions, messages: surveyMessages });
  };

  return (
    <Root>
      <Content>
        <Header>
          <StyledAutoAwesomeIcon />
          <Title>Configure Survey</Title>
        </Header>
        <ScrollArea>
          <SectionTitle>Questions</SectionTitle>
          {questions.map((q, i) => (
            <QuestionRow key={q.key}>
              <QuestionLabel>{q.key}</QuestionLabel>
              <QuestionInput
                value={q.text}
                onChange={(e) => updateQuestion(i, e.target.value)}
                rows={3}
              />
            </QuestionRow>
          ))}

          <SectionTitle>Message Testing</SectionTitle>
          <MessageTable>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Title</Th>
                <Th>Category</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {surveyMessages.map((msg) => (
                <Tr key={msg.id}>
                  <Td $width="40px">{msg.id}</Td>
                  <Td $truncate>{msg.title}</Td>
                  <Td $width="80px">{msg.category || '—'}</Td>
                  <Td $width="32px">
                    <RemoveBtn onClick={() => removeMessage(msg.id)} title="Remove message">
                      <XIcon size={14} />
                    </RemoveBtn>
                  </Td>
                </Tr>
              ))}
              {surveyMessages.length === 0 && (
                <Tr>
                  <Td colSpan={4} style={{ textAlign: 'center', color: '#999' }}>
                    No messages selected
                  </Td>
                </Tr>
              )}
            </tbody>
          </MessageTable>
        </ScrollArea>
        <Footer>
          <CancelButton onClick={onCancel}>Cancel</CancelButton>
          <SaveButton disabled={surveyMessages.length === 0} onClick={handleSave}>
            Save
          </SaveButton>
        </Footer>
      </Content>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
  border-radius: ${Constants.Modal.BorderRadius}px;
  height: 100%;
  width: 100%;
  position: relative;
`;

const Content = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--pri-01);
`;

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px 0;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 8px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin: 12px 0 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QuestionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
  align-items: flex-start;
`;

const QuestionLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #6366f1;
  min-width: 28px;
  padding-top: 8px;
`;

const QuestionInput = styled.textarea`
  flex: 1;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.5;
  resize: vertical;
  color: #333;
  background: #fff;
  min-height: 90px;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: #6366f1;
  }
`;

const MessageTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
`;

const Th = styled.th`
  text-align: left;
  padding: 6px 4px;
  font-size: 10px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 1px solid #e5e5e5;
`;

const Td = styled.td<{ $width?: string; $truncate?: boolean }>`
  padding: 6px 4px;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
  ${(p) => p.$width && `width: ${p.$width};`}
  ${(p) =>
    p.$truncate &&
    `
    max-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const Tr = styled.tr`
  &:hover {
    background: #fafafa;
  }
`;

const RemoveBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #999;
  cursor: pointer;

  &:hover {
    background: #fee2e2;
    color: #ef4444;
  }
`;

const Footer = styled.div`
  padding: 8px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const CancelButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #d3d3d3;
  border-radius: 6px;
  background: transparent;
  color: #626262;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #999;
    background: #fafafa;
  }
`;

const SaveButton = styled.button`
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  background: #6366f1;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: #4f46e5;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
